import { FF_PATIENT_DETAILS_SKELETON_ENABLED } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import type { DirtyFields } from '@/utils/reactHookForm';
import { dirtyValues } from '@/utils/reactHookForm';

import { Logger } from '@montu-web/utilities';
import { Card, CardContent, Toast, Typography } from '@montugroup/design-system';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import { GetPatientDetailsIncludeEnum } from '@montugroup/pms-api-contracts';
import { Stack, styled } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { FormSectionSkeleton } from './FormSectionSkeleton';
import type { FormSectionStatus } from './FormSectionStatusIcons';
import FormSectionStatusIcons from './FormSectionStatusIcons';
import { useUpdatePatientDetails } from './hooks/useUpdatePatientDetails';
import { PatientContext } from './PatientContext';

const borderStyle = {
  padding: 0,
  border: 0,
  boxShadow: 'none',
};

const CardContentClass = '.MuiCardContent-root';
const StyledCard = styled(Card, {
  shouldForwardProp: (props) => props !== 'noBorder',
})<{ noBorder: boolean }>(({ theme, noBorder }) => [
  {
    ...(noBorder && borderStyle),
    [theme.breakpoints.down('sm')]: {
      ...borderStyle,
      [CardContentClass]: { padding: `${theme.spacing(1)} 0` },
    },
    [CardContentClass]: noBorder ? { padding: `${theme.spacing(2)} 0` } : {},
  },
  theme.applyStyles('dark', {
    backgroundImage: 'none',
  }),
]);

type FormSectionProps<T extends FieldValues> = {
  /** Title heading for the card */
  title: string;
  /** React children */
  children: ReactNode;
  /**
   * Required for autosave to work. The values should be the following
   * "patient-basic-info", "patient-identification-info", "patient-medical-info", "patient-care-info", "patient-red-flag-info", "patient-emergency-contact
   */
  formName?: GetPatientDetailsInclude | undefined;
  /** React hook form context */
  formContext: UseFormReturn<T>;
  /** Option to remove border and box shadow */
  noBorder?: boolean;
  /**
   * Callback hook to submit the form data to local state
   * @deprecated in the future it will only trigger when the formName prop is not provided
   */
  onSubmit?: (formValues: T) => void;
  /**
   * Callback hook to process dirtyValues before passing as payload
   * @param dirtyValues - changed values from React hook form
   * @returns payload
   */
  transform?: (dirtyValues: T) => T;
  /**
   * A prop for displaying skeleton loaders
   */
  isLoading?: boolean;
  /**
   * A prop object for passing status to the form section autosave icon indicators
   */
  formSectionStatus?: FormSectionStatus;
  /**
   * A callback fn which is called after a successful mutation
   */
  onSuccess?: (formValues: T) => void;
};

const logger = new Logger('FormSection.ts');

export function FormSection<T extends FieldValues>({
  title,
  formContext,
  onSubmit,
  formName,
  children,
  noBorder = false,
  isLoading = false,
  transform,
  formSectionStatus,
  onSuccess,
  ...rest
}: FormSectionProps<T>) {
  const {
    reset,
    setError,
    getValues,
    formState: { isDirty, dirtyFields },
  } = formContext;
  const { flags } = useFeatureFlags();
  const isSkeletonEnabled = flags[FF_PATIENT_DETAILS_SKELETON_ENABLED];
  const { patientId } = useContext(PatientContext);
  const { isPending, isError, isSuccess, mutateAsync } = useUpdatePatientDetails(formName);
  const [retryFields, setRetryFields] = useState<DirtyFields<T> | null>(null);

  const serverErrorToastId = 'serverErrorToastId';

  const handleTransformation = useCallback(
    (payload: T) => (typeof transform === 'function' ? transform(payload) : payload),
    [transform],
  );

  const handleMutation = useCallback(
    async (fields: DirtyFields<T>, attributes: T) => {
      const payload = handleTransformation(
        dirtyValues({ ...(fields as DirtyFields<unknown>), ...retryFields }, attributes) as T,
      );

      try {
        await mutateAsync(patientId, payload);
        onSuccess && onSuccess(payload);
      } catch (responseError) {
        if (!Toast.isActive(serverErrorToastId)) {
          Toast.error(
            `Autosave ${
              (responseError as any)?.status === 500
                ? 'failed to save changes'
                : (responseError as any)?.body?.error?.message
            }`,
            { toastId: serverErrorToastId, autoClose: false },
          );
        }

        logger.error('Autosave:', responseError);

        setRetryFields((prev) => ({ ...prev, ...fields }));
      }
    },
    [handleTransformation, mutateAsync, onSuccess, patientId, retryFields],
  );

  const handleRetry = useCallback(async () => {
    const payload = handleTransformation(getValues());
    if (retryFields && payload) {
      handleMutation(retryFields as DirtyFields<T>, payload);
    }
  }, [getValues, handleMutation, handleTransformation, retryFields]);

  const submitHandler = useCallback(async () => {
    if (isDirty) {
      if (await formContext.trigger()) {
        const attributes = formContext.getValues();

        // TODO: remove this handler when autosave is implemented
        onSubmit && onSubmit(attributes);

        const isValidFormType = formName && GetPatientDetailsIncludeEnum.parse(formName);
        const hasPayload = Object.keys(attributes).length !== 0;

        if (isValidFormType && hasPayload) {
          if (dirtyFields) {
            handleMutation(dirtyFields as DirtyFields<T>, attributes);
          }
        }
      }
    }
  }, [formContext, isDirty, onSubmit, formName, dirtyFields, handleMutation]);

  useEffect(() => {
    if (retryFields) {
      for (const field in retryFields) {
        setError(field as unknown as Path<T>, { type: 'server', message: 'Not saved' });
      }
    }
  }, [retryFields, setError]);
  /**
   * Need to reset the form to clear the form isDirty flag and dirtyField
   * otherwise, it will continue to include those fields in the payload
   */
  useEffect(() => {
    if (!isPending && isSuccess) {
      reset(formContext.getValues());
      setRetryFields(null);
      Toast.dismiss(serverErrorToastId);
    }
  }, [formContext, isPending, isSuccess, reset]);

  const status = { isPending, isError, isSuccess, ...formSectionStatus };

  return (
    <StyledCard noBorder={noBorder} onBlur={submitHandler} {...rest}>
      <CardContent>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography component="h3" variant="h6" color="primary" sx={{ fontWeight: 600 }} data-dd-privacy="allow">
            {title}
          </Typography>
          <FormSectionStatusIcons showRetryButton={!!retryFields} retryFn={handleRetry} {...status} />
        </Stack>
        {isLoading && isSkeletonEnabled ? <FormSectionSkeleton>{children}</FormSectionSkeleton> : children}
      </CardContent>
    </StyledCard>
  );
}

export default FormSection;
